import React, { ReactElement, useEffect, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { MenuProps, SelectProps, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { App } from 'common/components/antd/App';
import { Button } from 'common/components/antd/Button/Button';
import { Select } from 'common/components/antd/Select/Select';
import { Table } from 'common/components/antd/Table/Table';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { IS_ADMIN_FE } from 'common/constants/general-access-role.constants';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'common/constants/table.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { useGetBranchesMultiRoleQuery_MultiRole } from 'domains/branches/queries/branch.query';
import {
  TCounterpartyAvailableBranchesResData,
  TCounterpartyBranchesResData,
} from 'domains/branches/types/branch.types';
import { useGetCounterpartiesQuery } from 'domains/counterparties/queries/counterparty.query';
import { TCounterpartiesListReqParams } from 'domains/counterparties/types/counterparty.types';
import { useGetEmployeesQuery_MultiRole } from 'domains/employees/queries/employees.query';
import { TEmployeesListResData, TCounterpartyBranchEmployeesResData } from 'domains/employees/types/employees.types';
import { TScopeResData } from 'domains/roles/types/roles.types';
import { getColumns } from './employees-table.columns';
import { SDangerActionItem, STopBar } from './employees.page.style';
import {
  ECounterpartyAction,
  EEmployeesColumnKey,
  EModalType,
} from './enums/employees.enum';
import CreateEditEmployeeModal from './modals/create-edit-employee.modal';
import {
  TEmployeesModalData,
  TEmployeesTableRecord,
  TTablePaginationConfig,
  TUserRoleManagementEmployeesLoaderData,
} from './types/employees.types';

const UserManagementEmployeesPage = (): ReactElement => {
  const { t } = useTranslation();
  const loaderData = useLoaderData() as TUserRoleManagementEmployeesLoaderData;
  const { notification } = App.useApp();
  const [selectedCounterparty, setSelectedCounterparty] = useState(ENV_COUNTERPARTY_GUID);
  const [modalData, setModalData] = useState<TEmployeesModalData | {}>({});
  const [paginationData, setPaginationData] = useState<TTablePaginationConfig>({
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [selectedBranch, setSelectedBranch] =
    useState<string>((loaderData.branchesData as TCounterpartyAvailableBranchesResData).branches?.[0].guid);

  const {
    data: counterpartiesData,
    isError: isCounterpartiesDataError,
    error: counterpartiesDataError,
  } = useGetCounterpartiesQuery();

  const {
    data: branchesData,
    isError: isBranchesDataError,
    error: branchesDataError,
  } = useGetBranchesMultiRoleQuery_MultiRole(
    selectedCounterparty,
    {},
    loaderData.hasAccessForAllBranches,
    {
      refetchOnMount: false,
      select: (data: any) => {
        const redoData = {
          ...data,
          ...(loaderData.hasAccessForAllBranches ? { branches: data.list } : null),
        };

        return redoData;
      },
    },
  );

  const {
    data: employeesData,
    isLoading: isEmployeesDataLoading,
    isError: isEmployeesDataError,
    error: employeesDataError,
  } = useGetEmployeesQuery_MultiRole(
    selectedCounterparty,
    selectedBranch,
    {
      limit: paginationData.pageSize,
      offset: (paginationData.page - 1) * paginationData.pageSize,
    },
    loaderData.hasAccessForAllBranches,
    {
      refetchOnMount: false,
      placeholderData: keepPreviousData,
    },
  );

  const isError = isCounterpartiesDataError || isBranchesDataError || isEmployeesDataError;
  const error = counterpartiesDataError || branchesDataError || employeesDataError;

  useEffect(() => {
    if (isError) {
      notification.error({
        message: (error as { message: string }).message,
        type: 'error',
      });
    }
  }, [isError, error]);

  const getCounterpartyName = (guid: string): string =>
    counterpartiesData?.counterparties.filter((counterparty) => counterparty.guid === guid)[0]?.name || '';

  const actions: MenuProps['items'] = [
    {
      key: ECounterpartyAction.Edit,
      label: t('usrRoleMng.employees.table.action.edit'),
    },
    {
      key: ECounterpartyAction.Delete,
      label: <SDangerActionItem>{t('usrRoleMng.employees.table.action.delete')}</SDangerActionItem>,
    },
  ];

  const onItemActionClick = ({ record, actionKey }: { record: TEmployeesTableRecord; actionKey: string }): void => {
    switch (actionKey) {
      case ECounterpartyAction.Edit: {
        setModalData({
          type: EModalType.Edit,
          rowData: record,
          branchesData,
          counterpartiesData,
        });
        break;
      }
      case ECounterpartyAction.Delete: {
        throw new Error('Deletion is not implemented');
        break;
      }
      default: {
        break;
      }
    }
  };

  const columns = getColumns({
    selectedCounterparty,
    menuItems: actions,
    onItemActionClick,
  });

  const employeesTableData: TEmployeesTableRecord[] | undefined = employeesData?.employees?.map(
    (item): TEmployeesTableRecord => ({
      user: `${item.name} ${item.surname}`,
      role: item.roles?.join(', '),
      email: item.login,
      phone: item.phone,
      counterparty: getCounterpartyName(item.counterparty_guid),
      branch_name: item.branch_name,
      status: '',
      name: item.name,
      surname: item.surname,
      paternal_name: item.paternal_name,
      counterparty_guid: item.counterparty_guid,
      employee_guid: item.guid,
    }),
  );

  const counterpartyOptions = (counterpartiesData?.counterparties || []).map((c) => ({
    label: c.name,
    value: c.guid,
  }));

  const selectedCounterpartyOption = counterpartyOptions.find((opt) => opt.value === selectedCounterparty);

  const onChangeBranchHandler = async (branch: string): Promise<void> => {
    setSelectedBranch(branch);
  };

  const handlePaginationChange = (pageParam: number, pageSizeParam: number): void => {
    setPaginationData({
      page: pageParam,
      pageSize: pageSizeParam,
    });
  };

  const onCancelModalHandler = (): void => {
    setModalData({});
  };

  const changeCounterparty: SelectProps['onChange'] = (value) => {
    setSelectedBranch('');
    setSelectedCounterparty(value);
  };

  return (
    <div>
      <STopBar>
        <Space style={{ padding: '16px' }}>
          {IS_ADMIN_FE && (
            <Select
              style={{
                width: 272,
              }}
              defaultValue={selectedCounterparty}
              value={selectedCounterpartyOption}
              options={counterpartyOptions}
              onChange={changeCounterparty}
            />
          )}
          <Select
            style={{ width: '280px' }}
            placeholder={t('usrRoleMng.employees.select.placeholder.branchName') as string}
            onChange={onChangeBranchHandler}
            allowClear={loaderData.hasAccessForAllBranches}
            value={selectedBranch || []}
          >
            {((branchesData as TCounterpartyAvailableBranchesResData)?.branches || []).map((option) => (
              <Select.Option key={option.guid} value={option.guid}>
                {option?.name}
              </Select.Option>
            ))}
          </Select>
        </Space>
        {!IS_ADMIN_FE
          || (IS_ADMIN_FE && selectedCounterparty === ENV_COUNTERPARTY_GUID) && (
          <Space style={{ padding: '16px' }}>
            <Button
              onClick={(): void => {
                setModalData({
                  type: EModalType.Create,
                  rowData: {},
                  selectedCounterparty,
                  branchesData,
                  counterpartiesData,
                });
              }}
              type="primary"
            >
              {t('usrRoleMng.employees.button.add')}
            </Button>
          </Space>
        )}
      </STopBar>
      <Table
        pagination={{
          style: {
            marginRight: 30,
          },
          showQuickJumper: true,
          showSizeChanger: true,
          total: employeesData?.total || 0,
          pageSize: paginationData.pageSize,
          current: paginationData.page,
          onChange: handlePaginationChange,
        }}
        rowKey={EEmployeesColumnKey.Guid}
        dataSource={employeesTableData}
        columns={columns}
        loading={isEmployeesDataLoading}
      />
      <CreateEditEmployeeModal
        isOpen={!!Object.keys(modalData).length}
        onCancelModalHandler={onCancelModalHandler}
        data={modalData as TEmployeesModalData}
      />
    </div>
  );
};

export const loader = async (): Promise<{
  hasAccessForAllBranches: boolean,
  employeesData: TCounterpartyBranchEmployeesResData | {},
  counterpartiesData: TCounterpartiesListReqParams | {},
  branchesData: TCounterpartyBranchesResData | {},
}> => {
  try {
    const counterpartiesDataQuery = queryKeysClient[EQueryKeys.CounterpartyQueryKeys].getCounterpartiesList();
    const counterpartiesData: TCounterpartiesListReqParams =
      queryClient.getQueryData(counterpartiesDataQuery.queryKey) ??
      (await queryClient.fetchQuery(counterpartiesDataQuery));

    const scopeListDataQuery =
      queryKeysClient[EQueryKeys.RbacQueryKeys].getCounterpartyScopesList(ENV_COUNTERPARTY_GUID);
    const scopeListData: TScopeResData =
      queryClient.getQueryData(scopeListDataQuery.queryKey) ??
      (await queryClient.fetchQuery(scopeListDataQuery));

    const hasAccessForAllBranches =
      ((scopeListData as TScopeResData).scopes.find(({ scope }) => scope === 'employees')?.is_allowed &&
        (scopeListData as TScopeResData).scopes.find(({ scope }) => scope === 'branches')?.is_allowed) ||
      false;

    const getBranchesMultiRoleQuery = queryKeysClient[EQueryKeys.BranchQueryKeys].getBranches_MultiRole(
      ENV_COUNTERPARTY_GUID,
      {},
      hasAccessForAllBranches,
    );
    const branchesData: TCounterpartyBranchesResData | TCounterpartyAvailableBranchesResData =
      queryClient.getQueryData(getBranchesMultiRoleQuery.queryKey) ??
      (await queryClient.fetchQuery(getBranchesMultiRoleQuery));

    const defaultBranchGUID = hasAccessForAllBranches
      ? ''
      : (branchesData as TCounterpartyAvailableBranchesResData).branches[0].guid;

    const getEmployeesMultiRoleQuery = queryKeysClient[EQueryKeys.EmployeesQueryKeys].getEmployees_MultiRole(
      {
        offset: (DEFAULT_PAGE - 1) * DEFAULT_PAGE_SIZE,
        limit: DEFAULT_PAGE_SIZE,
      },
      ENV_COUNTERPARTY_GUID,
      defaultBranchGUID,
      hasAccessForAllBranches,
    );

    const employeesData: TEmployeesListResData | TCounterpartyBranchEmployeesResData =
      queryClient.getQueryData(getEmployeesMultiRoleQuery.queryKey) ??
      (await queryClient.fetchQuery(getEmployeesMultiRoleQuery));

    return {
      hasAccessForAllBranches,
      branchesData,
      employeesData,
      counterpartiesData,
    };
  } catch (error) {
    console.error(error);

    return {
      hasAccessForAllBranches: false,
      branchesData: {},
      employeesData: {},
      counterpartiesData: {},
    };
  }
};

export default UserManagementEmployeesPage;
